import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { MetricChart } from "./MetricChart.js";
import { Spinner } from "./ui.js";

interface Series {
  metric: string;
  range: string;
  unit: string;
  bucket: "day" | "week";
  points: { date: string; value: number | null }[];
  count: number;
  sparse: boolean;
  note: string | null;
}

const METRICS: { key: string; label: string }[] = [
  { key: "weight", label: "Weight" },
  { key: "calories", label: "Calories" },
  { key: "protein", label: "Protein" },
  { key: "walk_minutes", label: "Walking" },
  { key: "workouts", label: "Workouts" },
  { key: "mood", label: "Mood" },
  { key: "energy", label: "Energy" },
  { key: "adherence", label: "Adherence" }
];

const RANGES: { key: string; label: string }[] = [
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "6m", label: "6m" },
  { key: "1y", label: "1y" },
  { key: "all", label: "All" }
];

export function TrendExplorer() {
  const [metric, setMetric] = useState("weight");
  const [range, setRange] = useState("30d");
  const [series, setSeries] = useState<Series | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get<{ series: Series }>(`/api/trends?metric=${metric}&range=${range}`)
      .then((r) => { if (active) setSeries(r.series); })
      .catch(() => { if (active) setSeries(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [metric, range]);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Trends</span>
      </div>

      <div className="chip-row" role="tablist" aria-label="Metric">
        {METRICS.map((m) => (
          <button key={m.key} type="button" className={`chip${metric === m.key ? " active" : ""}`} onClick={() => setMetric(m.key)}>
            {m.label}
          </button>
        ))}
      </div>

      <div className="segmented segmented-sm mt-3">
        {RANGES.map((r) => (
          <button key={r.key} type="button" className={range === r.key ? "active" : ""} onClick={() => setRange(r.key)}>
            {r.label}
          </button>
        ))}
      </div>

      <div className="mt-4" style={{ minHeight: 150 }}>
        {loading ? (
          <div className="center" style={{ padding: "var(--space-6)" }}><Spinner /></div>
        ) : !series ? (
          <div className="dim text-sm center" style={{ padding: "var(--space-6)" }}>Couldn't load this trend.</div>
        ) : series.sparse ? (
          <div className="dim text-sm center" style={{ padding: "var(--space-6)" }}>{series.note}</div>
        ) : (
          <MetricChart
            points={series.points}
            unit={series.unit === "%" ? "%" : ""}
            mode={series.bucket === "week" ? "bar" : "line"}
          />
        )}
      </div>
      {series && !series.sparse && (
        <p className="dim text-xs center" style={{ marginBottom: 0 }}>
          {series.count} data point{series.count === 1 ? "" : "s"} · {series.unit}
        </p>
      )}
    </div>
  );
}
